import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class LicensePlateValidator {
  /**
   * Valid USA state codes
   */
  static readonly USA_STATE_CODES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    'DC' // District of Columbia
  ];

  /**
   * Common USA license plate patterns with state codes:
   * - CA9DXD404 (State + Number + Letters + Numbers)
   * - CA123ABC (State + Numbers + Letters)
   * - CAABC123 (State + Letters + Numbers)
   * - CA1234567 (State + Numbers only)
   */
  static readonly USA_LICENSE_PLATE_PATTERNS = [
    /^[A-Z]{2}[0-9]{1}[A-Z]{3}[0-9]{3}$/,     // CA9DXD404 (State + 1 digit + 3 letters + 3 digits)
    /^[A-Z]{2}[0-9]{3}[A-Z]{3}$/,             // CA123ABC (State + 3 digits + 3 letters)
    /^[A-Z]{2}[A-Z]{3}[0-9]{3}$/,             // CAABC123 (State + 3 letters + 3 digits)
    /^[A-Z]{2}[A-Z]{2}[0-9]{4}$/,             // CAAB1234 (State + 2 letters + 4 digits)
    /^[A-Z]{2}[0-9]{4}[A-Z]{2}$/,             // CA1234AB (State + 4 digits + 2 letters)
    /^[A-Z]{2}[A-Z]{4}[0-9]{2}$/,             // CAABCD12 (State + 4 letters + 2 digits)
    /^[A-Z]{2}[0-9]{5,7}$/,                   // CA12345 to CA1234567 (State + 5-7 digits)
    /^[A-Z]{2}[A-Z]{1}[0-9]{6}$/,             // CAA123456 (State + 1 letter + 6 digits)
    /^[A-Z]{2}[A-Z]{2}[0-9]{3}$/,             // CAAB123 (State + 2 letters + 3 digits)
    /^[A-Z]{2}[0-9]{2}[A-Z]{3}[0-9]{1}$/,     // CA12ABC1 (State + 2 digits + 3 letters + 1 digit)
    /^[A-Z]{2}[0-9]{1}[A-Z]{2}[0-9]{4}$/,     // CA1AB2345 (State + 1 digit + 2 letters + 4 digits)
    /^[A-Z]{2}[A-Z]{3}[0-9]{4}$/              // CAABC1234 (State + 3 letters + 4 digits)
  ];

  /**
   * Validator function for USA license plates with state codes
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static usaLicensePlate(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const licensePlate = control.value.toString().trim().toUpperCase();
    
    // Check minimum length (state code + at least 3 characters)
    if (licensePlate.length < 5) {
      return { 
        usaLicensePlate: { 
          value: control.value,
          message: 'License plate must include state code (e.g., CA9DXD404).'
        } 
      };
    }

    // Check maximum length (state code + max 8 characters)
    if (licensePlate.length > 10) {
      return { 
        usaLicensePlate: { 
          value: control.value,
          message: 'License plate is too long.'
        } 
      };
    }

    // Check if it contains only valid characters (letters and numbers only, no spaces or dashes)
    if (!/^[A-Z0-9]+$/.test(licensePlate)) {
      return { 
        usaLicensePlate: { 
          value: control.value,
          message: 'License plate can only contain letters and numbers.'
        } 
      };
    }

    // Extract state code (first 2 characters)
    const stateCode = licensePlate.substring(0, 2);
    
    // Validate state code
    if (!LicensePlateValidator.USA_STATE_CODES.includes(stateCode)) {
      return { 
        usaLicensePlate: { 
          value: control.value,
          message: `Invalid state code '${stateCode}'. Use a valid US state code.`
        } 
      };
    }

    // Check if it matches any valid USA license plate pattern
    const isValidPattern = LicensePlateValidator.USA_LICENSE_PLATE_PATTERNS.some(
      pattern => pattern.test(licensePlate)
    );

    if (!isValidPattern) {
      return { 
        usaLicensePlate: { 
          value: control.value,
          message: 'Invalid license plate format (e.g., CA9DXD404).'
        } 
      };
    }

    return null; // Valid license plate
  }

  /**
   * Format license plate to standard format (uppercase, remove spaces/dashes)
   * @param licensePlate - Raw license plate string
   * @returns Formatted license plate or original if invalid
   */
  static formatLicensePlate(licensePlate: string): string {
    if (!licensePlate) return licensePlate;

    // Convert to uppercase and remove all spaces and dashes
    const formatted = licensePlate.trim().toUpperCase().replace(/[\s-]/g, '');
    
    return formatted;
  }

  /**
   * Check if license plate is valid USA format
   * @param licensePlate - License plate to validate
   * @returns boolean
   */
  static isValidUsaLicensePlate(licensePlate: string): boolean {
    if (!licensePlate) return false;
    
    const control = { value: licensePlate } as AbstractControl;
    return LicensePlateValidator.usaLicensePlate(control) === null;
  }

  /**
   * Get suggestions for common license plate formats with state codes
   * @returns Array of example formats
   */
  static getFormatExamples(): string[] {
    return [
      'CA9DXD404',
      'CA123ABC',
      'CAABC123',
      'CAAB1234',
      'CA1234AB',
      'CA12345',
      'TX7ABC123'
    ];
  }
}

/**
 * Factory function to create USA license plate validator
 * @returns ValidatorFn
 */
export function usaLicensePlateValidator(): ValidatorFn {
  return LicensePlateValidator.usaLicensePlate;
}
